import { FC, useState } from "react";

import { useFormikContext } from "formik";
import { useTranslation } from "next-i18next";

import Select from "@components/Select";
import { useRouter } from "@lib/router";

import loadAddressDetails from "../services/loadAddressDetails";
import loadAddressOptions from "../services/loadAddressOptions";
import type { TAddressDetails } from "../types";

const ADDRESS_OPTION_PREFIX = "search-address-";

type TStreetInputProps = {
  id: string;
  name: string;
  countryCode: string;
  onAddressSelected: (item: TAddressDetails) => void;
  onError?: () => void;
};

const StreetInput: FC<TStreetInputProps> = ({ id, name, countryCode, onAddressSelected, onError }) => {
  const { t } = useTranslation();

  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  const {
    values: { [name]: streetValue },
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
  } = useFormikContext<any>();

  const { defaultLocale } = useRouter();

  const handleLoadAddresses = async (query: string) => {
    const addresses = await loadAddressOptions({
      query,
      countryCode,
      defaultLocale: defaultLocale!,
      errCallback: onError,
    });

    const options = addresses.map(({ id: addressId, title }) => ({
      value: `${ADDRESS_OPTION_PREFIX}${addressId}`,
      label: title,
    }));
    return options;
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  const handleStreetChanged = async (value: any) => {
    if (value.indexOf(ADDRESS_OPTION_PREFIX) === 0) {
      setIsDetailsLoading(true);

      const addressId = value.substring(ADDRESS_OPTION_PREFIX.length);

      const addressDetails = await loadAddressDetails({
        id: addressId,
        defaultLocale: defaultLocale!,
        errCallback: onError,
      });

      if (addressDetails) {
        onAddressSelected(addressDetails);
        setFieldValue(name, addressDetails.street || "");
      }

      setIsDetailsLoading(false);
      return;
    }

    setFieldValue(name, value);
  };

  return (
    <Select
      isFormik={false}
      name={name}
      label={t("address_fields-street")}
      value={streetValue}
      id={id}
      loadOptions={handleLoadAddresses}
      onChange={handleStreetChanged}
      onBlur={handleBlur}
      isInvalid={!!errors[name] && !!touched[name]}
      message={errors[name] as string}
      isLoading={isDetailsLoading}
      mode="customAsyncOption"
    />
  );
};
export default StreetInput;
